/**
 * AuthPage - Authentication page for login and sign up.
 *
 * Renders Clerk's SignIn/SignUp UI and handles navigation for authentication flows.
 * Used for user onboarding and access control.
 */
import { assets } from "../assets/assets";
import { SignIn, SignUp } from "@clerk/react";
import { useNavigate } from "react-router-dom";

interface AuthPageProps {
  isSignUp?: boolean;
}

function AuthPage({ isSignUp = false }: AuthPageProps) {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen overflow-y-scroll">
      {/* Form Section */}
      <div className="relative w-full lg:w-3/6 h-screen flex flex-col justify-center items-center px-4 sm:px-8 lg:px-12 bg-white mx-auto">
        {/* Main content */}
        <div className="flex-1 flex flex-col justify-center items-center mx-auto w-full max-w-md">
          <h2 className="text-center text-4xl font-semibold font-serif italic">
            {isSignUp ? "Sign Up" : "Login"}
          </h2>
          <p className="text-sm sm:text-md  my-1 mb-4 sm:mb-6 text-center">
            {isSignUp
              ? "Welcome to Lurna! Sign up to get started."
              : "Welcome back to Lurna! Sign in to continue."}
          </p>

          {/* Clerk Auth UI */}
          <div className="flex flex-col items-center justify-center w-full">
            {isSignUp ? (
              <SignUp routing="path" path="/signup" signInUrl="/login" fallbackRedirectUrl="/" />
            ) : (
              <SignIn routing="path" path="/login" signUpUrl="/signup" fallbackRedirectUrl="/" />
            )}
          </div>
          {isSignUp ? (
            <p className="text-xs text-center">
              Already have an account? <span onClick={() => navigate('/login')} className="underline underline-offset-2 italic cursor-pointer">Log in</span>
            </p>
          ) : (
            <p className="text-xs text-center">
              Don't have an account? <span onClick={() => navigate('/signup')} className="underline underline-offset-2 italic cursor-pointer">Sign up</span>
            </p>
          )}
        </div>

        {/* Terms fixed at bottom */}
        <p className="mb-4 sm:mb-5 bottom-0 w-full text-center text-xs text-muted-foreground px-2">
          By continuing, you agree to Lurna's{" "}
          <button
            className="text-foreground font-medium underline cursor-pointer"
            onClick={() => window.open("https://lurna.co/terms", "_blank")}
          >
            Terms of Service
          </button>{" "}
          and{" "}
          <button
            className="text-foreground font-medium underline cursor-pointer"
            onClick={() => window.open("https://lurna.co/privacy", "_blank")}
          >
            Privacy Policy
          </button>
          .
        </p>
      </div>

      {/* Image Section */}
      <div className="hidden lg:block w-full h-screen overflow-hidden bg-cover bg-center bg-white p-2">
        <img
          className="w-full h-full object-cover rounded-4xl"
          src={assets.authpageimg}
          alt="Auth background"
        />
      </div>

      <div className="absolute lg:hidden top-3 left-3">
        <img
          className="size-23 invert dark:invert-0"
          src={assets.lurna_white_logo}
          alt="Auth logo"
        />
      </div>
    </div>
  );
}

export default AuthPage;
